import { HTTP } from '@community_dev/requests'
import { CamelCasedPropertiesDeep } from 'type-fest'

import { Capability, Role } from './clients'

import { ENDPOINTS } from 'constants/endpoints'
import { request } from 'utils/request'
import { route } from 'utils/router'

export type FeatureGroup = {
  id: string
  name: string
  deprecated?: boolean
  description?: string
  capabilities: Capability[]
  createdAt: string
}

export type FeatureGroupClient = {
  firstName: string
  lastName: string
  id: string
  clientImage: string
  phoneNumber: string
}

export function getRefreshToken() {
  return request(route(ENDPOINTS.LOGIN.REFRESH_TOKEN))
}

export const getCapabilities = ({ prefix = 'feature' }: { prefix: string }): Promise<Capability[]> => {
  return request(route(ENDPOINTS.CAPABILITIES.CAPABILITIES, { prefix })).then((data) => data.data)
}

// The capabilities service lists capabilities by prefix only.
export const ROLE_CAPABILITY_PREFIXES = ['admin', 'client', 'data', 'feature', 'global']

export const getRoleCapabilityCatalog = async (prefixes: string[]): Promise<Capability[]> => {
  const capabilityLists = await Promise.all(prefixes.map((prefix) => getCapabilities({ prefix })))
  return capabilityLists.flat()
}

export enum RoleVisibility {
  ADMIN = 'admin',
  PRIVATE = 'private',
  PUBLIC = 'public',
}

export type RoleDetails = Role & {
  visibility: RoleVisibility
  updatedAt: string
  seatMappingCount: number
}

export type RoleUpdate = {
  displayName: string
  description: string
  capabilityNames: string[]
}

export const getRole = (roleName: string): Promise<RoleDetails> => {
  return request(route(ENDPOINTS.CAPABILITIES.ROLE, { roleName: encodeURIComponent(roleName) })).then(
    (data) => data.data,
  )
}

export const updateRole = (roleName: string, update: RoleUpdate): Promise<unknown> => {
  return request(route(ENDPOINTS.CAPABILITIES.ROLE, { roleName: encodeURIComponent(roleName) }), {
    method: HTTP.PUT,
    body: update,
    ignoreReportingErrorCodes: [422],
  })
}

export const getFeatureGroups = (): Promise<FeatureGroup[]> => {
  return request(route(ENDPOINTS.CAPABILITIES.FEATURE_GROUPS)).then((data) => data.data)
}

export const postFeatureGroup = (name: string): Promise<any> => {
  return request(route(ENDPOINTS.CAPABILITIES.FEATURE_GROUP, { featureGroupName: name }), {
    method: HTTP.POST,
  })
}

export type PatchFeatureGroupResponse = CamelCasedPropertiesDeep<{
  data: {
    created_at: string
    id: string
    name: string
  }
}>

export const patchFeatureGroup = (
  featureGroupName: string,
  body: { name?: string; deprecated_at?: string },
): Promise<PatchFeatureGroupResponse> => {
  return request(route(ENDPOINTS.CAPABILITIES.FEATURE_GROUP, { featureGroupName: featureGroupName }), {
    method: HTTP.PATCH,
    body,
  })
}

export const getClientsByFeatureGroup = (name: string): Promise<FeatureGroupClient[]> => {
  return request(route(ENDPOINTS.CAPABILITIES.FEATURE_GROUP_CLIENTS, { featureGroupName: name })).then(
    (data) => data.data,
  )
}

export const getClientFeatureGroups = (id: string): Promise<FeatureGroup[]> => {
  return request(route(ENDPOINTS.CLIENTS.FEATURE_GROUPS, { clientId: id })).then((data) => data.data)
}

export function postCapability({ name, description }: { name: string; description: string }): Promise<Capability> {
  return request(route(ENDPOINTS.CAPABILITIES.CAPABILITY, { capabilityName: name }), {
    method: HTTP.POST,
    body: {
      description,
    },
  }).then((res) => res.data)
}

export function deleteFeatureGroup({ name }: { name: string }): Promise<any> {
  return request(route(ENDPOINTS.CAPABILITIES.FEATURE_GROUP, { featureGroupName: name }), {
    method: HTTP.DELETE,
  })
}

export function deleteCapability({ name }: { name: string }): Promise<any> {
  return request(route(ENDPOINTS.CAPABILITIES.CAPABILITY, { capabilityName: name }), {
    method: HTTP.DELETE,
  })
}

export type PostClientFeatureGroupResponse = CamelCasedPropertiesDeep<{
  data: {
    client_id: string
    feature_group_id: string
    feature_group_name: string
  }
}>

export function postClientFeatureGroup({
  id,
  featureGroupName,
}: {
  id: string
  featureGroupName: string
}): Promise<PostClientFeatureGroupResponse> {
  return request(
    route(ENDPOINTS.CLIENTS.FEATURE_GROUP, {
      clientId: id,
      featureGroupName,
    }),
    {
      method: HTTP.POST,
    },
  )
}

export function deleteClientFeatureGroup({
  id,
  featureGroupName,
}: {
  id: string
  featureGroupName: string
}): Promise<void> {
  return request(
    route(ENDPOINTS.CLIENTS.FEATURE_GROUP, {
      clientId: id,
      featureGroupName,
    }),
    {
      method: HTTP.DELETE,
    },
  )
}

export function postCapabilityToFeatureGroup({
  featureGroupName,
  capabilityName,
}: {
  featureGroupName: string
  capabilityName: string
}): Promise<FeatureGroup> {
  return request(route(ENDPOINTS.CAPABILITIES.FEATURE_GROUP_CAPABILITIES, { featureGroupName }), {
    method: HTTP.POST,
    body: {
      capabilities: [capabilityName],
    },
  })
}

export function deleteCapabilityFromFeatureGroup({
  featureGroupName,
  capabilityName,
}: {
  featureGroupName: string
  capabilityName: string
}): Promise<void> {
  return request(route(ENDPOINTS.CAPABILITIES.FEATURE_GROUP_CAPABILITY, { featureGroupName, capabilityName }), {
    method: HTTP.DELETE,
  })
}
